import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Pause,
  HelpCircle,
  RefreshCw,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const columns = [
  { id: 'pending',    label: 'Pending',    icon: Clock,        color: 'border-yellow-300 dark:border-yellow-500/50 bg-yellow-50 dark:bg-yellow-500/5',    ring: 'ring-yellow-400' },
  { id: 'in_review',  label: 'In Review',  icon: AlertCircle,  color: 'border-blue-300 dark:border-blue-500/50 bg-blue-50 dark:bg-blue-500/5',            ring: 'ring-blue-400' },
  { id: 'approved',   label: 'Approved',   icon: CheckCircle,  color: 'border-emerald-300 dark:border-emerald-500/50 bg-emerald-50 dark:bg-emerald-500/5', ring: 'ring-emerald-400' },
  { id: 'rejected',   label: 'Rejected',   icon: XCircle,      color: 'border-red-300 dark:border-red-500/50 bg-red-50 dark:bg-red-500/5',                ring: 'ring-red-400' },
  { id: 'on_hold',    label: 'On Hold',    icon: Pause,        color: 'border-orange-300 dark:border-orange-500/50 bg-orange-50 dark:bg-orange-500/5',     ring: 'ring-orange-400' },
  { id: 'needs_info', label: 'Needs Info', icon: HelpCircle,   color: 'border-purple-300 dark:border-purple-500/50 bg-purple-50 dark:bg-purple-500/5',     ring: 'ring-purple-400' },
];

const priorityColors = {
  high:   'bg-red-500',
  medium: 'bg-yellow-500',
  low:    'bg-green-500',
};

const formatCurrency = (amount, currency = 'INR') => {
  const symbols = { INR: '₹', USD: '$', EUR: '€', JPY: '¥', CNY: '¥' };
  return `${symbols[currency] || currency} ${amount?.toLocaleString() || 0}`;
};

// ── Card content (pure visual, no dnd hooks) ──────────────────────────────────

const CardContent = memo(function CardContent({ card, className = '' }) {
  return (
    <div className={`bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-3 select-none ${className}`}>
      <div className="flex items-start justify-between mb-2">
        <span className="font-mono text-sm text-zinc-900 dark:text-white">{card.part_number}</span>
        <div
          className={`w-2 h-2 rounded-full ${priorityColors[card.priority] || 'bg-zinc-500'}`}
          title={card.priority}
        />
      </div>
      <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-2 line-clamp-1">{card.part_name}</p>
      <div className="flex items-center justify-between text-xs">
        <span className="text-zinc-500">{card.family_name}</span>
        <span className="text-zinc-900 dark:text-white font-medium">
          {formatCurrency(card.dealer_price, card.currency)}
        </span>
      </div>
      {card.due_date && (
        <div className={`text-xs mt-2 ${new Date(card.due_date) < new Date() ? 'text-red-500 dark:text-red-400' : 'text-zinc-500'}`}>
          Due: {new Date(card.due_date).toLocaleDateString()}
        </div>
      )}
      <div className="text-xs text-zinc-400 dark:text-zinc-600 mt-2">
        By: {card.created_by_name}
      </div>
    </div>
  );
});

// ── Sortable Card ──────────────────────────────────────────────────────────────

const KanbanCard = memo(function KanbanCard({ card, navigate }) {
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: card.id,
    data: { type: 'card', card },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={() => !isDragging && navigate(`/cogs-cards/${card.id}`)}
      style={{
        opacity: isDragging ? 0.35 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
        pointerEvents: isDragging ? 'none' : 'auto',
      }}
      className="shadow-sm rounded-lg"
    >
      <CardContent card={card} />
    </div>
  );
});

// ── Droppable Column ───────────────────────────────────────────────────────────

const KanbanColumn = memo(function KanbanColumn({ column, cards, navigate, isDragging }) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });
  const Icon = column.icon;
  const isDroppingHere = isOver && isDragging;
  const cardIds = useMemo(() => cards.map(c => c.id), [cards]);

  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-72 rounded-lg border transition-all duration-150
        ${column.color}
        ${isDroppingHere ? `ring-2 ${column.ring}` : ''}
      `}
    >
      <div className="p-3 border-b border-zinc-200 dark:border-zinc-800/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-zinc-700 dark:text-zinc-300" />
            <span className="font-medium text-zinc-900 dark:text-white">{column.label}</span>
          </div>
          <span className="text-xs bg-zinc-200 dark:bg-zinc-800 px-2 py-1 rounded text-zinc-600 dark:text-zinc-400">
            {cards.length}
          </span>
        </div>
      </div>

      <SortableContext items={cardIds} strategy={verticalListSortingStrategy}>
        <div
          className={`p-2 space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto min-h-[80px] transition-colors duration-150
            ${isDroppingHere ? 'bg-white/40 dark:bg-white/5' : ''}
          `}
        >
          {cards.length === 0 ? (
            <div className="text-center py-8 text-zinc-400 dark:text-zinc-600 text-sm">
              No cards
            </div>
          ) : (
            cards.map(card => (
              <KanbanCard key={card.id} card={card} navigate={navigate} />
            ))
          )}
        </div>
      </SortableContext>
    </div>
  );
});

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function KanbanPage() {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [familyFilter, setFamilyFilter] = useState('all');
  const [families, setFamilies] = useState([]);
  const [viewMode, setViewMode] = useState('all');
  const [activeCard, setActiveCard] = useState(null);
  const originalStatusRef = useRef(null);
  const currentStatusRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  useEffect(() => {
    fetchCards();
    fetchFamilies();
  }, [viewMode]);

  const fetchCards = async () => {
    try {
      setLoading(true);
      const endpoint = viewMode === 'my' ? `${API}/cogs-cards/my-tasks` : `${API}/cogs-cards`;
      const response = await axios.get(endpoint);
      setCards(response.data);
    } catch (error) {
      toast.error('Failed to fetch COGS cards');
    } finally {
      setLoading(false);
    }
  };

  const fetchFamilies = async () => {
    try {
      const response = await axios.get(`${API}/families`);
      setFamilies(response.data);
    } catch (error) {
      console.error('Failed to fetch families');
    }
  };

  // Single pass — bucket all cards by status
  const cardsByStatus = useMemo(() => {
    const map = Object.fromEntries(columns.map(c => [c.id, []]));
    for (const card of cards) {
      if (familyFilter !== 'all' && card.family_id !== familyFilter) continue;
      if (map[card.status]) map[card.status].push(card);
    }
    return map;
  }, [cards, familyFilter]);

  const handleDragStart = useCallback(({ active }) => {
    const card = cards.find(c => c.id === active.id);
    setActiveCard(card || null);
    originalStatusRef.current = card?.status;
    currentStatusRef.current = card?.status;
  }, [cards]);

  const handleDragOver = useCallback(({ active, over }) => {
    if (!over || active.id === over.id) return;

    const isOverColumn = columns.some(c => c.id === over.id);

    setCards(prev => {
      const activeIdx = prev.findIndex(c => c.id === active.id);
      if (activeIdx === -1) return prev;
      const activeCard = prev[activeIdx];

      if (isOverColumn) {
        // Hovering over empty column space — move to end of that column
        currentStatusRef.current = over.id;
        const without = prev.filter(c => c.id !== active.id);
        const lastInCol = without.reduce((acc, c, i) => c.status === over.id ? i : acc, -1);
        without.splice(lastInCol + 1, 0, { ...activeCard, status: over.id });
        return without;
      }

      // Hovering over another card — insert at that card's position
      const overIdx = prev.findIndex(c => c.id === over.id);
      if (overIdx === -1) return prev;

      const newStatus = prev[overIdx].status;
      currentStatusRef.current = newStatus;

      const next = [...prev];
      next.splice(activeIdx, 1);
      const adjustedOverIdx = next.findIndex(c => c.id === over.id);
      // If active was above over in the list, insert after; otherwise insert before
      const insertAfter = activeIdx < overIdx;
      next.splice(insertAfter ? adjustedOverIdx + 1 : adjustedOverIdx, 0, { ...activeCard, status: newStatus });
      return next;
    });
  }, []);

  const handleDragEnd = useCallback(async ({ active }) => {
    setActiveCard(null);
    const original = originalStatusRef.current;
    const final = currentStatusRef.current;
    if (!final || final === original) return;

    const columnLabel = columns.find(c => c.id === final)?.label;
    try {
      await axios.put(`${API}/cogs-cards/${active.id}`, { status: final });
      toast.success(`Moved to ${columnLabel}`);
    } catch (error) {
      setCards(prev => prev.map(c => c.id === active.id ? { ...c, status: original } : c));
      toast.error('Failed to update status');
    }
  }, []);

  const handleDragCancel = useCallback(({ active }) => {
    setCards(prev => prev.map(c => c.id === active.id ? { ...c, status: originalStatusRef.current } : c));
    setActiveCard(null);
  }, []);

  return (
    <MainLayout>
      <div className="p-8 space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900 dark:text-white">Kanban Board</h1>
            <p className="text-zinc-500 dark:text-zinc-400 mt-1">Drag cards between columns to update their status</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={viewMode} onValueChange={setViewMode}>
              <SelectTrigger className="w-40 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700">
                <Eye className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800">
                <SelectItem value="all">All Cards</SelectItem>
                <SelectItem value="my">My Assigned</SelectItem>
              </SelectContent>
            </Select>
            <Select value={familyFilter} onValueChange={setFamilyFilter}>
              <SelectTrigger className="w-44 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700">
                <SelectValue placeholder="Filter by Family" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800">
                <SelectItem value="all">All Families</SelectItem>
                {families.map(family => (
                  <SelectItem key={family.id} value={family.id}>{family.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchCards}
              className="border-zinc-300 dark:border-zinc-700"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-[#C3002F] border-t-transparent rounded-full" />
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <div className="flex gap-4 overflow-x-auto pb-4">
              {columns.map(column => (
                <KanbanColumn
                  key={column.id}
                  column={column}
                  cards={cardsByStatus[column.id]}
                  navigate={navigate}
                  isDragging={!!activeCard}
                />
              ))}
            </div>

            <DragOverlay dropAnimation={null}>
              {activeCard ? (
                <CardContent card={activeCard} className="shadow-xl ring-2 ring-[#C3002F]/40 rotate-1 cursor-grabbing" />
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>
    </MainLayout>
  );
}
